import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Activity,
    Zap,
    ShieldCheck,
    Tv,
    BadgeCheck,
    Gauge,
} from "lucide-react";
import { FiberCanvas } from "../components/FiberCanvas";
import { AnimatedLogo } from "../components/AnimatedLogo";
import { Marquee } from "../components/Marquee";
import { ComboBanner } from "../components/ComboBanner";
import { FadeUp, MaskedLine, Counter } from "../components/Reveal";
import { BRAND, PLANS } from "../data/siteData";

const BENEFITS = [
    {
        icon: Activity,
        title: "99.9% Uptime",
        text: "Reliable FTTH connectivity designed for everyday performance.",
    },
    {
        icon: Zap,
        title: "High-Speed FTTH",
        text: "Fast and stable fiber internet for home and business usage.",
    },
    {
        icon: ShieldCheck,
        title: "15+ Years of Trust",
        text: "Serving customers with long-term local experience.",
    },
    {
        icon: Tv,
        title: "Entertainment Included",
        text: "TV channels + OTT entertainment options available.",
    },
];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-[100svh] items-center overflow-hidden"
        >
            <div className="absolute inset-0" aria-hidden="true">
                <img
                    src="https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwY2FibGUlMjB0ZWNobm9sb2d5JTIwc3BlZWQlMjBjb25uZWN0aW9uJTIwaW50ZXJuZXQlMjBuZXR3b3JrfGVufDB8fHx8MTc5MTM1NjY4OHww&ixlib=rb-4.1.0&q=85"
                    alt=""
                    className="h-full w-full object-cover opacity-25"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19]/70 via-[#0B0F19]/85 to-[#0B0F19]" />
            </div>
            <FiberCanvas className="absolute inset-0 h-full w-full" />
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="pointer-events-none absolute right-6 top-1/2 hidden w-[270px] -translate-y-1/2 lg:block xl:right-14 xl:w-[320px]"
                data-testid="hero-logo-showcase"
            >
                <div className="animate-float">
                    <AnimatedLogo />
                </div>
            </motion.div>
            <div
                className="pointer-events-none absolute -left-40 top-1/3 h-[30rem] w-[30rem] rounded-full bg-[#E61C24]/20 blur-[140px]"
                aria-hidden="true"
            />
            <div
                className="pointer-events-none absolute -right-40 top-10 h-[26rem] w-[26rem] rounded-full bg-[#00F0FF]/10 blur-[140px]"
                aria-hidden="true"
            />

            <motion.div
                style={{ y, opacity }}
                className="relative mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8"
            >
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.05 }}
                >
                    <span
                        data-testid="hero-trust-badge"
                        className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold tracking-wide text-slate-200 backdrop-blur-md sm:text-sm"
                    >
                        <BadgeCheck className="h-4 w-4 text-[#00F0FF]" />
                        {BRAND.trustLine}
                    </span>
                </motion.div>

                <h1
                    data-testid="hero-headline"
                    className="font-display mt-8 text-5xl font-black leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-8xl"
                >
                    <MaskedLine delay={0.15}>FAST INTERNET.</MaskedLine>
                    <MaskedLine delay={0.32} className="text-gradient-fire pb-2">
                        STRONGER CONNECTIONS.
                    </MaskedLine>
                </h1>

                <motion.p
                    data-testid="hero-subtext"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55 }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                >
                    Experience reliable high-speed FTTH broadband with 99.9%
                    uptime and entertainment that keeps your entire family
                    connected.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <Link
                        to="/get-connected"
                        data-testid="hero-get-connection-button"
                        className="group inline-flex items-center gap-3 rounded-full bg-[#E61C24] px-8 py-4 text-base font-extrabold text-white shadow-[0_10px_44px_rgba(230,28,36,0.5)] transition-[transform,background-color,box-shadow] duration-200 hover:scale-105 hover:bg-[#C7151C]"
                    >
                        Get New Connection
                        <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                    <Link
                        to="/plans"
                        data-testid="hero-view-plans-button"
                        className="inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/5 px-8 py-4 text-base font-bold text-white backdrop-blur-md transition-[border-color,color,transform] duration-200 hover:scale-105 hover:border-[#00F0FF]/70 hover:text-[#00F0FF]"
                    >
                        View Plans
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                    className="mt-14 grid max-w-2xl grid-cols-3 gap-3 sm:gap-5"
                >
                    {[
                        { value: 15, suffix: "+", label: "Years of Trust", decimals: 0 },
                        { value: 99.9, suffix: "%", label: "Uptime", decimals: 1 },
                        { value: 100, suffix: "", label: "Mbps FTTH", decimals: 0 },
                    ].map((s) => (
                        <div
                            key={s.label}
                            className="glass rounded-2xl px-4 py-4 sm:px-5"
                            data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <p className="font-mono text-xl font-bold text-white sm:text-3xl">
                                <Counter to={s.value} suffix={s.suffix} decimals={s.decimals} />
                            </p>
                            <p className="mt-1 text-[11px] font-medium tracking-wide text-slate-400 sm:text-xs">
                                {s.label}
                            </p>
                        </div>
                    ))}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.15 }}
                    className="mt-8 inline-flex items-center gap-3 rounded-full border border-emerald-400/25 bg-emerald-400/5 px-4 py-2"
                    data-testid="hero-network-status"
                >
                    <span className="animate-pulse-dot h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <span className="font-mono text-xs font-semibold tracking-widest text-emerald-300">
                        NETWORK LIVE · NARASARAOPET
                    </span>
                    <Gauge className="h-4 w-4 text-emerald-300" />
                </motion.div>
            </motion.div>
        </section>
    );
};

const QuickBenefits = () => (
    <section data-testid="quick-benefits" className="relative -mt-10 pb-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-5 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
            {BENEFITS.map((b, i) => (
                <FadeUp key={b.title} delay={i * 0.08}>
                    <div
                        data-testid={`benefit-card-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="glass group h-full rounded-3xl p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-[#E61C24]/40 hover:shadow-[0_16px_48px_rgba(230,28,36,0.18)]"
                    >
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E61C24]/15 text-[#ff5a60] transition-transform duration-300 group-hover:scale-110">
                            <b.icon className="h-6 w-6" />
                        </div>
                        <h3 className="font-display mt-5 text-lg font-bold text-white">
                            {b.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-400">
                            {b.text}
                        </p>
                    </div>
                </FadeUp>
            ))}
        </div>
    </section>
);

const PlansTeaser = () => (
    <section data-testid="home-plans-teaser" className="relative py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <FadeUp>
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="font-mono text-xs font-semibold tracking-[0.3em] text-[#00F0FF]">
                            BROADBAND PLANS
                        </p>
                        <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            Choose Your Perfect Speed
                        </h2>
                    </div>
                    <Link
                        to="/plans"
                        data-testid="home-view-all-plans"
                        className="group inline-flex items-center gap-2 text-sm font-bold text-[#ff5a60] transition-colors duration-200 hover:text-white"
                    >
                        View all plans
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                </div>
            </FadeUp>

            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {PLANS.map((p, i) => (
                    <FadeUp key={p.id} delay={i * 0.08}>
                        <Link
                            to={`/get-connected?plan=${p.speed}`}
                            data-testid={`home-plan-card-${p.id}mbps`}
                            className={`group block h-full rounded-3xl border p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 ${
                                p.highlight
                                    ? "border-[#E61C24]/50 bg-gradient-to-b from-[#E61C24]/15 to-[#131927] shadow-[0_0_40px_rgba(230,28,36,0.18)]"
                                    : "glass hover:border-white/25"
                            }`}
                        >
                            <p className="font-mono text-[11px] font-bold tracking-[0.28em] text-[#00F0FF]">
                                {p.tag}
                            </p>
                            <p className="font-display mt-3 text-4xl font-black text-white">
                                {p.speed}
                                <span className="ml-1 text-base font-bold text-slate-400">
                                    Mbps
                                </span>
                            </p>
                            <p className="mt-2 text-sm text-slate-400">{p.blurb}</p>
                            <p className="mt-5 font-mono text-2xl font-bold text-white">
                                ₹{p.monthly}
                                <span className="text-sm font-medium text-slate-400">
                                    {" "}
                                    / Month
                                </span>
                            </p>
                            <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#ff5a60] transition-colors duration-200 group-hover:text-white">
                                Get This Plan
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                            </span>
                        </Link>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);

const FinalCTA = () => (
    <section data-testid="home-final-cta" className="relative overflow-hidden py-20 sm:py-28">
        <div
            className="pointer-events-none absolute left-1/2 top-1/2 h-[26rem] w-[46rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E61C24]/12 blur-[130px]"
            aria-hidden="true"
        />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
            <FadeUp>
                <h2 className="font-display text-3xl font-black tracking-tight text-white sm:text-5xl">
                    Ready to Get Connected?
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-base text-slate-400 sm:text-lg">
                    Experience reliable FTTH internet with Eway Fiber Net.
                </p>
            </FadeUp>
            <FadeUp delay={0.15}>
                <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                    <a
                        href={BRAND.phones[0].tel}
                        data-testid="home-cta-call"
                        className="rounded-full border border-white/20 px-7 py-3.5 text-sm font-bold text-white transition-[border-color,color,transform] duration-200 hover:scale-105 hover:border-[#00F0FF]/70 hover:text-[#00F0FF]"
                    >
                        Call Now
                    </a>
                    <a
                        href={BRAND.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="home-cta-whatsapp"
                        className="rounded-full bg-[#1FAF54] px-7 py-3.5 text-sm font-bold text-white transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-[#178f43]"
                    >
                        WhatsApp
                    </a>
                    <Link
                        to="/get-connected"
                        data-testid="home-cta-get-connected"
                        className="rounded-full bg-[#E61C24] px-7 py-3.5 text-sm font-extrabold text-white shadow-[0_10px_40px_rgba(230,28,36,0.45)] transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-[#C7151C]"
                    >
                        Get New Connection
                    </Link>
                </div>
            </FadeUp>
        </div>
    </section>
);

export default function Home() {
    return (
        <>
            <Hero />
            <QuickBenefits />
            <Marquee />
            <PlansTeaser />
            <ComboBanner />
            <FinalCTA />
        </>
    );
}
